// src/pages/admin/AdminReports.tsx
import { useState, useEffect, useMemo } from 'react';
import {
  Container, Box, Typography, Grid, Paper, Button, Card, CardContent,
  TextField, MenuItem, useTheme, alpha, Divider, Chip, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Alert,
  Tabs, Tab, FormControl, InputLabel, Select, Checkbox, FormControlLabel, Collapse,
  Avatar, LinearProgress, AlertTitle
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import {
  Download as DownloadIcon,
  Assessment as AssessmentIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  Message as MessageIcon,
  Store as MarketplaceIcon,
  TrendingUp as TrendingUpIcon,
  PictureAsPdf as PdfIcon,
  Inventory as InventoryIcon,
  Science as ScienceIcon,
  CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Settings as SettingsIcon,
  Analytics as AnalyticsIcon
} from '@mui/icons-material';
import {
  getUserReport,
  getConnectionsReport,
  getMarketplaceReport,
  getEngagementReport,
  type UserReportData,
  type ConnectionReportData,
  type MarketplaceReportData,
  type EngagementReportData
} from '../../services/reports';
import { getDashboardStats, type DashboardStats } from '../../services/analytics';
import * as industryApi from '../../services/industry';
import type { Industry } from '../../services/industry';

const MotionCard = motion(Card);
const MotionBox = motion(Box);
const MotionPaper = motion(Paper);

const SectionHeader = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -8,
    left: 0,
    width: 60,
    height: 4,
    borderRadius: 2,
    backgroundColor: theme.palette.primary.main,
  }
}));

const StyledFormControlLabel = styled(FormControlLabel)(({ theme }) => ({
  margin: '8px 0',
  '& .MuiFormControlLabel-label': {
    fontSize: '0.9rem',
    fontWeight: 500,
  },
  '& .MuiCheckbox-root': {
    borderRadius: 8,
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.04),
    }
  },
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.02),
    borderRadius: 8,
    padding: '4px 8px',
    margin: '4px -8px',
    transition: 'all 0.2s ease'
  }
}));

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const AdminReports = () => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [reportType, setReportType] = useState('users');
  const [dateRange, setDateRange] = useState('last-30-days');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Platform report data states
  const [reportData, setReportData] = useState<any[]>([]);
  const [summaryStats, setSummaryStats] = useState<DashboardStats | null>(null);

  // Industry report states
  const [industries, setIndustries] = useState<Industry[]>([]);
  const [industriesLoading, setIndustriesLoading] = useState(true);
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);
  const [selectedSubIndustries, setSelectedSubIndustries] = useState<string[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    insights: true,
    valueChain: true,
    tabs: false
  });
  const [selectedSections, setSelectedSections] = useState({
    // Industry Insights
    summary: false,
    description: false,
    growthRate: false,
    marketSize: false,
    investmentOpportunities: false,
    keyMetrics: false,
    growthData: false,

    // Value Chain & Ecosystem
    valueChain: false,
    supportEcosystem: false,

    // Tabs (Deep Content)
    marketOverview: false,
    legalInfrastructure: false,
    trends: false,
    incentives: false,

    // Sub-Industries
    includeSubIndustries: false
  });

  const reportTypes = [
    { value: 'users', label: 'User Report', icon: <PeopleIcon /> },
    { value: 'connections', label: 'Connections Report', icon: <MessageIcon /> },
    { value: 'marketplace', label: 'Marketplace Report', icon: <MarketplaceIcon /> },
    { value: 'engagement', label: 'Engagement Report', icon: <TrendingUpIcon /> },
  ];

  const dateRanges = [
    { value: 'today', label: 'Today' },
    { value: 'last-7-days', label: 'Last 7 Days' },
    { value: 'last-30-days', label: 'Last 30 Days' },
    { value: 'last-90-days', label: 'Last 90 Days' },
    { value: 'this-year', label: 'This Year' },
  ];

  // Load initial data
  useEffect(() => {
    loadSummaryStats();
    loadReportData();
    loadIndustries();
  }, []);

  // Reload report data when type or date range changes
  useEffect(() => {
    if (reportType && tabValue === 0) {
      loadReportData();
    }
  }, [reportType, dateRange]);

  const loadSummaryStats = async () => {
    try {
      const stats = await getDashboardStats();
      setSummaryStats(stats);
    } catch (err: any) {
      console.error('Error loading summary stats:', err);
    }
  };

  const loadReportData = async () => {
    try {
      setLoading(true);
      setError(null);

      let data: any[] = [];
      switch (reportType) {
        case 'users':
          data = await getUserReport(dateRange);
          break;
        case 'connections':
          data = await getConnectionsReport(dateRange);
          break;
        case 'marketplace':
          data = await getMarketplaceReport(dateRange);
          break;
        case 'engagement':
          data = await getEngagementReport(dateRange);
          break;
        default:
          data = [];
      }

      setReportData(data);
    } catch (err: any) {
      console.error('Error loading report data:', err);
      const errorMessage = typeof err.response?.data?.error === 'string'
        ? err.response.data.error
        : err.response?.data?.error?.message || err.message || 'Failed to load report data';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const loadIndustries = async () => {
    try {
      setIndustriesLoading(true);
      const data = await industryApi.getAllIndustries({
        includeSubIndustries: true
      });
      setIndustries(data);
    } catch (err) {
      console.error('Error fetching industries:', err);
    } finally {
      setIndustriesLoading(false);
    }
  };

  const availableSubIndustries = useMemo(() => {
    if (selectedIndustries.length !== 1) return [];
    const industry = industries.find(ind => ind.id === selectedIndustries[0]);
    return industry?.hasSubIndustries && industry.subIndustries ? industry.subIndustries : [];
  }, [selectedIndustries, industries]);

  const categories = useMemo(() => {
    const uniqueCategories = Array.from(new Set(industries.map(ind => ind.category).filter(Boolean)));
    return uniqueCategories;
  }, [industries]);

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Localization': return '#6366f1';
      case 'Service & Infrastructure': return '#06b6d4';
      case 'Mature Industries': return '#10b981';
      default: return '#6366f1';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Localization': return <BusinessIcon />;
      case 'Service & Infrastructure': return <InventoryIcon />;
      case 'Mature Industries': return <ScienceIcon />;
      default: return <BusinessIcon />;
    }
  };

  const handleIndustryChange = (event: any) => {
    const value = typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value;
    setSelectedIndustries(value);
    setSelectedSubIndustries([]);
  };

  const handleSubIndustryChange = (event: any) => {
    const value = typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value;
    setSelectedSubIndustries(value);
  };

  const handleSectionChange = (section: keyof typeof selectedSections) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedSections(prev => ({
      ...prev,
      [section]: event.target.checked
    }));
  };

  const toggleSectionExpansion = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const isValidForGeneration = () => {
    return selectedIndustries.length > 0 && Object.values(selectedSections).some(selected => selected);
  };

  const getSelectedIndustriesData = () => {
    return industries.filter(ind => selectedIndustries.includes(ind.id));
  };

  const getSelectedSubIndustriesData = () => {
    if (selectedIndustries.length !== 1 || selectedSubIndustries.length === 0) return [];
    const industry = industries.find(ind => ind.id === selectedIndustries[0]);
    if (!industry?.subIndustries) return [];
    return industry.subIndustries.filter(sub => selectedSubIndustries.includes(sub.id));
  };

  const generatePDF = async () => {
    if (!isValidForGeneration()) return;

    setIsGenerating(true);

    try {
      // Dynamically import jsPDF
      const { jsPDF } = await import('jspdf');

      const selectedIndustriesData = getSelectedIndustriesData();
      const selectedSubIndustriesData = getSelectedSubIndustriesData();
      const currentDate = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });

      const doc = new jsPDF();
      let yPosition = 20;
      const pageHeight = doc.internal.pageSize.height;
      const pageWidth = doc.internal.pageSize.width;
      const margin = 20;

      // Helper function to add new page if needed
      const checkPageBreak = (requiredSpace: number): void => {
        if (yPosition + requiredSpace > pageHeight - margin) {
          doc.addPage();
          yPosition = 20;
        }
      };

      // Helper function to add text with word wrapping
      interface AddTextOptions {
        fontSize?: number;
        maxWidth?: number;
        fontStyle?: 'normal' | 'bold';
        color?: [number, number, number];
      }

      const addText = (text: string, x: number, y: number, options: AddTextOptions = {}): number => {
        const fontSize = options.fontSize || 10;
        const maxWidth = options.maxWidth || 170;
        const fontStyle = options.fontStyle || 'normal';
        const color = options.color || [0, 0, 0];

        doc.setFontSize(fontSize);
        doc.setFont('helvetica', fontStyle);
        doc.setTextColor(...color);

        const lines: string[] = doc.splitTextToSize(text, maxWidth);
        doc.text(lines, x, y);
        return lines.length * (fontSize * 0.35);
      };

      // Add header with logo to each page
      const addHeader = () => {
        // Header background
        doc.setFillColor(37, 99, 235); // Blue color
        doc.rect(0, 0, pageWidth, 25, 'F');

        // Logo placeholder (you would load actual logo here)
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text('SAUDI VISION 2030', margin, 16);

        // Reset colors
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'normal');

        return 35; // Return starting Y position after header
      };

      // Cover Page
      yPosition = addHeader();

      // Main title with gradient effect simulation
      doc.setFontSize(32);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(37, 99, 235);
      doc.text('INDUSTRY REPORT', pageWidth / 2, yPosition + 30, { align: 'center' });

      doc.setFontSize(20);
      doc.setTextColor(75, 85, 99);
      doc.text('Saudi Arabia Economic Analysis', pageWidth / 2, yPosition + 50, { align: 'center' });

      // Decorative line
      doc.setDrawColor(37, 99, 235);
      doc.setLineWidth(2);
      doc.line(pageWidth / 2 - 40, yPosition + 60, pageWidth / 2 + 40, yPosition + 60);

      // Date and generation info
      doc.setFontSize(12);
      doc.setTextColor(107, 114, 128);
      doc.text(`Generated on ${currentDate}`, pageWidth / 2, yPosition + 80, { align: 'center' });

      // Report summary box
      yPosition += 100;
      doc.setFillColor(249, 250, 251);
      doc.setDrawColor(229, 231, 235);
      doc.roundedRect(margin, yPosition, pageWidth - 2 * margin, 60, 5, 5, 'FD');

      yPosition += 15;
      doc.setFontSize(14);
      doc.setTextColor(75, 85, 99);
      doc.setFont('helvetica', 'bold');
      doc.text('Report Summary', margin + 10, yPosition);

      yPosition += 15;
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(107, 114, 128);

      const summaryLines = [
        `Industries Analyzed: ${selectedIndustriesData.length}`,
        `Sub-industries: ${selectedSubIndustriesData.length}`,
        `Report Sections: ${Object.values(selectedSections).filter(Boolean).length}`,
        `Total Pages: Estimated ${Math.ceil(selectedIndustriesData.length * 2.5)}`
      ];

      summaryLines.forEach((line, index) => {
        doc.text(`• ${line}`, margin + 10, yPosition + (index * 8));
      });

      // Save the PDF
      const fileName = `Admin_Industry_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);

    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleExport = (format: string) => {
    console.log(`Exporting ${reportType} report as ${format}`);
    alert(`Exporting ${reportType} report as ${format.toUpperCase()}...\n\nThis feature will be implemented soon.`);
  };

  const handleGenerateReport = () => {
    loadReportData();
  };

  const getReportIcon = (type: string) => {
    const report = reportTypes.find(r => r.value === type);
    return report?.icon || <AssessmentIcon />;
  };

  const sectionGroups = [
    {
      key: 'insights',
      title: 'Industry Insights',
      icon: <TrendingUpIcon />,
      color: theme.palette.primary.main,
      sections: [
        { key: 'summary', label: 'Executive Summary', description: 'High-level industry overview' },
        { key: 'description', label: 'Detailed Description', description: 'Comprehensive industry analysis' },
        { key: 'growthRate', label: 'Growth Rate', description: 'Annual growth percentage' },
        { key: 'marketSize', label: 'Market Size', description: 'Total market valuation' },
        { key: 'investmentOpportunities', label: 'Investment Opportunities', description: 'Available investment options' },
        { key: 'keyMetrics', label: 'Key Performance Indicators', description: 'Employment, exports, companies' },
        { key: 'growthData', label: 'Historical Growth Data', description: 'Multi-year growth trends' }
      ]
    },
    {
      key: 'valueChain',
      title: 'Value Chain & Ecosystem',
      icon: <AssessmentIcon />,
      color: theme.palette.secondary.main,
      sections: [
        { key: 'valueChain', label: 'Value Chain Analysis', description: 'Upstream, midstream, downstream' },
        { key: 'supportEcosystem', label: 'Support Ecosystem', description: 'Talent, funding, supply chain' }
      ]
    },
    {
      key: 'tabs',
      title: 'Deep Market Analysis',
      icon: <AnalyticsIcon />,
      color: theme.palette.info.main,
      sections: [
        { key: 'marketOverview', label: 'Market Overview', description: 'Comprehensive market analysis' },
        { key: 'legalInfrastructure', label: 'Legal Infrastructure', description: 'Regulatory framework' },
        { key: 'trends', label: 'Market Trends', description: 'Current and future trends' },
        { key: 'incentives', label: 'Government Incentives', description: 'Available support programs' }
      ]
    }
  ];

  // Render table headers based on report type
  const renderTableHeaders = () => {
    switch (reportType) {
      case 'users':
        return (
          <>
            <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
            <TableCell align="center" sx={{ fontWeight: 600 }}>Connections</TableCell>
            <TableCell align="center" sx={{ fontWeight: 600 }}>Messages</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Joined</TableCell>
          </>
        );
      case 'connections':
        return (
          <>
            <TableCell sx={{ fontWeight: 600 }}>Requester</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Receiver</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Requested At</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Responded At</TableCell>
          </>
        );
      case 'marketplace':
        return (
          <>
            <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Submitter</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Industry</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
          </>
        );
      case 'engagement':
        return (
          <>
            <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
            <TableCell align="center" sx={{ fontWeight: 600 }}>Messages Sent</TableCell>
            <TableCell align="center" sx={{ fontWeight: 600 }}>Connections</TableCell>
            <TableCell align="center" sx={{ fontWeight: 600 }}>Engagement Score</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Last Login</TableCell>
          </>
        );
      default:
        return null;
    }
  };

  // Render table rows based on report type
  const renderTableRows = () => {
    if (loading) {
      return (
        <TableRow>
          <TableCell colSpan={10} align="center">
            <CircularProgress />
          </TableCell>
        </TableRow>
      );
    }

    if (error) {
      return (
        <TableRow>
          <TableCell colSpan={10} align="center">
            <Alert severity="error">{error}</Alert>
          </TableCell>
        </TableRow>
      );
    }

    if (reportData.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={10} align="center">
            No data available for the selected date range
          </TableCell>
        </TableRow>
      );
    }

    switch (reportType) {
      case 'users':
        return reportData.map((row: UserReportData) => (
          <TableRow key={row.id} hover>
            <TableCell>{row.name}</TableCell>
            <TableCell>{row.email}</TableCell>
            <TableCell>
              <Chip
                label={row.type}
                size="small"
                color={row.type === 'Expert' ? 'primary' : 'default'}
              />
            </TableCell>
            <TableCell>
              <Chip
                label={row.status}
                size="small"
                color={row.status === 'Approved' ? 'success' : 'warning'}
              />
            </TableCell>
            <TableCell align="center">{row.connections}</TableCell>
            <TableCell align="center">{row.messages}</TableCell>
            <TableCell>{row.joined}</TableCell>
          </TableRow>
        ));
      case 'connections':
        return reportData.map((row: ConnectionReportData) => (
          <TableRow key={row.id} hover>
            <TableCell>{row.requester}<br/><Typography variant="caption" color="text.secondary">{row.requesterEmail}</Typography></TableCell>
            <TableCell>{row.receiver}<br/><Typography variant="caption" color="text.secondary">{row.receiverEmail}</Typography></TableCell>
            <TableCell>
              <Chip
                label={row.status}
                size="small"
                color={row.status === 'Accepted' ? 'success' : row.status === 'Rejected' ? 'error' : 'warning'}
              />
            </TableCell>
            <TableCell>{row.requestedAt}</TableCell>
            <TableCell>{row.respondedAt}</TableCell>
          </TableRow>
        ));
      case 'marketplace':
        return reportData.map((row: MarketplaceReportData) => (
          <TableRow key={row.id} hover>
            <TableCell>
              <Chip label={row.type} size="small" color="info" />
            </TableCell>
            <TableCell>{row.title}</TableCell>
            <TableCell>{row.submitter}<br/><Typography variant="caption" color="text.secondary">{row.email}</Typography></TableCell>
            <TableCell>{row.industry}</TableCell>
            <TableCell>
              <Chip
                label={row.status}
                size="small"
                color={row.status === 'Approved' ? 'success' : 'warning'}
              />
            </TableCell>
            <TableCell>{row.createdAt}</TableCell>
          </TableRow>
        ));
      case 'engagement':
        return reportData.map((row: EngagementReportData) => (
          <TableRow key={row.id} hover>
            <TableCell>{row.name}<br/><Typography variant="caption" color="text.secondary">{row.email}</Typography></TableCell>
            <TableCell>
              <Chip
                label={row.type}
                size="small"
                color={row.type === 'Expert' ? 'primary' : 'default'}
              />
            </TableCell>
            <TableCell align="center">{row.messagesSent}</TableCell>
            <TableCell align="center">{row.totalConnections}</TableCell>
            <TableCell align="center">
              <Chip label={row.engagementScore} size="small" color="success" />
            </TableCell>
            <TableCell>{row.lastLogin}</TableCell>
          </TableRow>
        ));
      default:
        return null;
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Hero Section */}
      <MotionPaper
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        elevation={0}
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          color: 'white',
          p: 4,
          borderRadius: 3,
          mb: 4
        }}
      >
        <Typography variant="h3" component="h1" fontWeight="bold" mb={2}>
          Reports & Analytics
        </Typography>
        <Typography variant="h6" sx={{ opacity: 0.9 }}>
          Generate comprehensive platform and industry reports
        </Typography>
      </MotionPaper>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MotionCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            elevation={2}
            sx={{ borderRadius: 3 }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main
                  }}
                >
                  <PeopleIcon />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Users
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {summaryStats?.users.total || 0}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip label={`+${summaryStats?.users.new || 0} New`} size="small" color="success" />
                <Chip label={`${summaryStats?.users.active || 0} Active`} size="small" />
              </Box>
            </CardContent>
          </MotionCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MotionCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            elevation={2}
            sx={{ borderRadius: 3 }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.success.main, 0.1),
                    color: theme.palette.success.main
                  }}
                >
                  <MessageIcon />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Connections
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {summaryStats?.connections.total || 0}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip label={`+${summaryStats?.connections.thisMonth || 0} This Month`} size="small" color="success" />
                <Chip label={`${summaryStats?.connections.pending || 0} Pending`} size="small" color="warning" />
              </Box>
            </CardContent>
          </MotionCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MotionCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            elevation={2}
            sx={{ borderRadius: 3 }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.info.main, 0.1),
                    color: theme.palette.info.main
                  }}
                >
                  <MessageIcon />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Messages
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {summaryStats?.messages.total || 0}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip label={`+${summaryStats?.messages.thisMonth || 0} This Month`} size="small" color="success" />
                <Chip label={`${summaryStats?.messages.averagePerUser || 0} Avg/User`} size="small" />
              </Box>
            </CardContent>
          </MotionCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MotionCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            elevation={2}
            sx={{ borderRadius: 3 }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.warning.main, 0.1),
                    color: theme.palette.warning.main
                  }}
                >
                  <MarketplaceIcon />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Marketplace
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {summaryStats?.marketplace.total || 0}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip label={`${summaryStats?.marketplace.active || 0} Active`} size="small" color="success" />
                <Chip label={`${summaryStats?.marketplace.pending || 0} Pending`} size="small" color="warning" />
              </Box>
            </CardContent>
          </MotionCard>
        </Grid>
      </Grid>

      {/* Main Tabs */}
      <Paper elevation={2} sx={{ borderRadius: 3, mb: 4 }}>
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab label="Platform Reports" icon={<AssessmentIcon />} iconPosition="start" />
          <Tab label="Industry Reports" icon={<BusinessIcon />} iconPosition="start" />
        </Tabs>

        {/* Platform Reports Tab */}
        <TabPanel value={tabValue} index={0}>
          {/* Report Configuration */}
          <Paper elevation={0} sx={{ p: 3, mb: 3, bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Configure Report
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  label="Report Type"
                  value={reportType}
                  onChange={(e) => setReportType(e.target.value)}
                >
                  {reportTypes.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {option.icon}
                        {option.label}
                      </Box>
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  label="Date Range"
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                >
                  {dateRanges.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', gap: 2, height: '100%', alignItems: 'flex-end' }}>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleGenerateReport}
                    startIcon={<AssessmentIcon />}
                  >
                    Generate Report
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Report Preview */}
          <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {getReportIcon(reportType)}
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {reportTypes.find(r => r.value === reportType)?.label} Preview
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleExport('csv')}
                  size="small"
                >
                  CSV
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleExport('pdf')}
                  size="small"
                >
                  PDF
                </Button>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleExport('excel')}
                  size="small"
                >
                  Excel
                </Button>
              </Box>
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                    {renderTableHeaders()}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {renderTableRows()}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="body2" color="text.secondary">
                {!loading && !error && reportData.length > 0
                  ? `Showing ${reportData.length} ${reportData.length === 1 ? 'entry' : 'entries'}`
                  : 'No entries to display'
                }
              </Typography>
            </Box>
          </Paper>
        </TabPanel>

        {/* Industry Reports Tab */}
        <TabPanel value={tabValue} index={1}>
          {industriesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress size={60} />
            </Box>
          ) : (
            <Grid container spacing={4}>
              {/* Configuration Panel */}
              <Grid item xs={12} md={4}>
                <Card sx={{ position: 'sticky', top: 20 }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={3}>
                      <SettingsIcon sx={{ mr: 2, color: theme.palette.primary.main }} />
                      <Typography variant="h6" fontWeight="bold">
                        Report Configuration
                      </Typography>
                    </Box>

                    {/* Industry Selection */}
                    <Box mb={3}>
                      <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                        Select Industries
                      </Typography>
                      <FormControl fullWidth>
                        <InputLabel>Choose Industries</InputLabel>
                        <Select
                          multiple
                          value={selectedIndustries}
                          onChange={handleIndustryChange}
                          renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selected.map((value) => {
                                const industry = industries.find(ind => ind.id === value);
                                return (
                                  <Chip
                                    key={value}
                                    label={industry?.name || value}
                                    size="small"
                                    sx={{
                                      bgcolor: getCategoryColor(industry?.category || ''),
                                      color: 'white'
                                    }}
                                  />
                                );
                              })}
                            </Box>
                          )}
                        >
                          {categories.map((category) => [
                            <MenuItem key={category} disabled sx={{
                              fontWeight: 'bold',
                              color: getCategoryColor(category),
                              backgroundColor: alpha(getCategoryColor(category), 0.1)
                            }}>
                              {getCategoryIcon(category)}
                              <Box ml={1}>{category}</Box>
                            </MenuItem>,
                            ...industries
                              .filter(industry => industry.category === category)
                              .map((industry) => (
                                <MenuItem key={industry.id} value={industry.id}>
                                  <Box ml={3}>{industry.name}</Box>
                                </MenuItem>
                              ))
                          ])}
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Sub-Industry Selection */}
                    {availableSubIndustries.length > 0 && (
                      <Box mb={3}>
                        <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                          Select Sub-Industries
                        </Typography>
                        <FormControl fullWidth>
                          <InputLabel>Choose Sub-Industries</InputLabel>
                          <Select
                            multiple
                            value={selectedSubIndustries}
                            onChange={handleSubIndustryChange}
                            renderValue={(selected) => (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((value) => {
                                  const subIndustry = availableSubIndustries.find(sub => sub.id === value);
                                  return (
                                    <Chip
                                      key={value}
                                      label={subIndustry?.name || value}
                                      size="small"
                                      color="secondary"
                                    />
                                  );
                                })}
                              </Box>
                            )}
                          >
                            {availableSubIndustries.map((subIndustry) => (
                              <MenuItem key={subIndustry.id} value={subIndustry.id}>
                                {subIndustry.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    )}

                    {/* Report Sections */}
                    <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                      Select Report Sections
                    </Typography>

                    {sectionGroups.map((group) => (
                      <Box key={group.key} mb={2}>
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{
                            cursor: 'pointer',
                            p: 1,
                            borderRadius: 1,
                            '&:hover': {
                              bgcolor: alpha(group.color, 0.05)
                            }
                          }}
                          onClick={() => toggleSectionExpansion(group.key as keyof typeof expandedSections)}
                        >
                          <Box display="flex" alignItems="center">
                            <Box sx={{ color: group.color, mr: 1 }}>
                              {group.icon}
                            </Box>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {group.title}
                            </Typography>
                          </Box>
                          {expandedSections[group.key as keyof typeof expandedSections] ?
                            <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </Box>

                        <Collapse in={expandedSections[group.key as keyof typeof expandedSections]}>
                          <Box ml={4} mt={1}>
                            {group.sections.map((section) => (
                              <StyledFormControlLabel
                                key={section.key}
                                control={
                                  <Checkbox
                                    checked={selectedSections[section.key as keyof typeof selectedSections]}
                                    onChange={handleSectionChange(section.key as keyof typeof selectedSections)}
                                    size="small"
                                  />
                                }
                                label={
                                  <Box>
                                    <Typography variant="body2" fontWeight="medium">
                                      {section.label}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {section.description}
                                    </Typography>
                                  </Box>
                                }
                              />
                            ))}
                          </Box>
                        </Collapse>
                      </Box>
                    ))}

                    {/* Sub-Industries Toggle */}
                    {availableSubIndustries.length > 0 && (
                      <Box mt={2}>
                        <StyledFormControlLabel
                          control={
                            <Checkbox
                              checked={selectedSections.includeSubIndustries}
                              onChange={handleSectionChange('includeSubIndustries')}
                              size="small"
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                Include Sub-Industries
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Add detailed sub-industry analysis
                              </Typography>
                            </Box>
                          }
                        />
                      </Box>
                    )}

                    <Divider sx={{ my: 3 }} />

                    {/* Generate Button */}
                    <Button
                      variant="contained"
                      fullWidth
                      size="large"
                      onClick={generatePDF}
                      disabled={!isValidForGeneration() || isGenerating}
                      startIcon={isGenerating ? <CircularProgress size={20} /> : <PdfIcon />}
                      sx={{
                        py: 1.5,
                        fontWeight: 'bold',
                        background: isValidForGeneration() ?
                          `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)` :
                          undefined
                      }}
                    >
                      {isGenerating ? 'Generating Report...' : 'Generate PDF Report'}
                    </Button>

                    {isGenerating && (
                      <Box mt={2}>
                        <LinearProgress />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                          Creating your comprehensive industry report...
                        </Typography>
                      </Box>
                    )}

                    {!isValidForGeneration() && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        <AlertTitle>Configuration Required</AlertTitle>
                        Please select at least one industry and one report section to generate a report.
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Preview Panel */}
              <Grid item xs={12} md={8}>
                <Card>
                  <CardContent>
                    <SectionHeader>
                      <Typography variant="h5" fontWeight="bold">
                        Report Preview
                      </Typography>
                    </SectionHeader>

                    {selectedIndustries.length === 0 ? (
                      <Box textAlign="center" py={6}>
                        <BusinessIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" mb={1}>
                          No Industries Selected
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Select industries from the configuration panel to preview your report
                        </Typography>
                      </Box>
                    ) : (
                      <Box>
                        {/* Selected Industries Preview */}
                        <Typography variant="h6" fontWeight="bold" mb={2}>
                          Selected Industries ({selectedIndustries.length})
                        </Typography>
                        <Grid container spacing={2} mb={4}>
                          {getSelectedIndustriesData().map((industry) => (
                            <Grid item xs={12} sm={6} key={industry.id}>
                              <Paper
                                elevation={1}
                                sx={{
                                  p: 2,
                                  border: `2px solid ${getCategoryColor(industry.category || '')}`,
                                  borderRadius: 2
                                }}
                              >
                                <Box display="flex" alignItems="center" mb={1}>
                                  <Avatar
                                    sx={{
                                      bgcolor: getCategoryColor(industry.category || ''),
                                      width: 32,
                                      height: 32,
                                      mr: 2
                                    }}
                                  >
                                    {getCategoryIcon(industry.category || '')}
                                  </Avatar>
                                  <Box>
                                    <Typography variant="subtitle1" fontWeight="bold">
                                      {industry.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {industry.category}
                                    </Typography>
                                  </Box>
                                </Box>

                                {industry.marketSize && (
                                  <Typography variant="body2" color="text.secondary">
                                    Market Size: ${industry.marketSize}
                                  </Typography>
                                )}

                                {industry.growthRate !== undefined && (
                                  <Typography variant="body2" color="text.secondary">
                                    Growth Rate: {industry.growthRate}%
                                  </Typography>
                                )}
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>

                        {/* Report Sections Preview */}
                        <Typography variant="h6" fontWeight="bold" mb={2}>
                          Report Sections ({Object.values(selectedSections).filter(Boolean).length})
                        </Typography>
                        <Grid container spacing={1} mb={4}>
                          {Object.entries(selectedSections)
                            .filter(([_, selected]) => selected)
                            .map(([key, _]) => {
                              const section = sectionGroups
                                .flatMap(group => group.sections)
                                .find(s => s.key === key);

                              if (!section) return null;

                              return (
                                <Grid item key={key}>
                                  <Chip
                                    icon={<CheckCircleIcon />}
                                    label={section.label}
                                    color="primary"
                                    variant="outlined"
                                    size="small"
                                  />
                                </Grid>
                              );
                            })}
                        </Grid>

                        {/* Estimated Report Info */}
                        <Box
                          sx={{
                            p: 3,
                            bgcolor: alpha(theme.palette.primary.main, 0.05),
                            borderRadius: 2,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                          }}
                        >
                          <Typography variant="h6" fontWeight="bold" mb={2}>
                            Estimated Report Details
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">
                                Total Pages
                              </Typography>
                              <Typography variant="h6" fontWeight="bold">
                                {Math.ceil(selectedIndustries.length * 2.5 + selectedSubIndustries.length * 1.5)}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">
                                Sections
                              </Typography>
                              <Typography variant="h6" fontWeight="bold">
                                {Object.values(selectedSections).filter(Boolean).length}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default AdminReports;
